/**
 * villageMapping.js — the ONE place a `GET /api/village` payload becomes Village props.
 *
 * Its sibling `approvalsMapping.js` exists because the screen and the backend describe the same
 * week in different vocabularies. This one exists for a sharper reason: **`VillageProjectCard`
 * defaults four fields to sentences about a family it has never met.**
 *
 * | the card's default | what the server sends |
 * | --- | --- |
 * | `eyebrow = 'Village project · this week'` | `started_at`, and no end date at all |
 * | `unit = 'lanterns'` | approval POINTS (`village_stats.earned_points`) |
 * | `rewardName = 'Festival Stage'`, `rewardOutcome = 'rises in the square — for keeps.'` | nothing |
 * | `contributorsLabel = 'Who lit what'` | totals only — the payload names nobody |
 *
 * **So the rule is the same as its sibling's: send what the server sent, and OVERRIDE what it did
 * not.** Note the difference in the verb. Omitting a field here re-selects the default, which is
 * the invention. `reward: null` is used rather than `reward: undefined` because the card documents
 * `false`/`null` as "hide this footer" and treats `undefined` as "use the default".
 */

/** A project whose name the server did not send. Never "undefined", never blank. */
const UNNAMED_PROJECT = 'Your family’s village project';

/** `175`, `'175'` and `null` all have to become a number the card can render, or nothing. */
function count(value) {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/**
 * `GET /api/village` → the Village project card, or `null`.
 *
 * `null` in, `null` out, and that is an ANSWER rather than a gap: `village.routes.js:27` states
 * that `village: null` means no project has been started. The page renders no card at all for a
 * null project, so nothing invented reaches the screen.
 */
export function toVillageProject(payload) {
  const village = payload && payload.village;
  if (!village || typeof village !== 'object') return null;

  const project = {
    // "Village project" alone. The card would otherwise date this goal to "this week", and
    // `village_projects` has a start and no end.
    eyebrow: 'Village project',
    title: (typeof village.name === 'string' && village.name.trim()) || UNNAMED_PROJECT,
    // The unit the server actually counts. `village_stats.earned_points` sums approval points.
    unit: 'points',
    // `null` HIDES the reward footer, which is what the card's own docblock promises. Leaving it
    // absent would print "When it's done: the Festival Stage rises in the square — for keeps."
    // about a family that was never offered a Festival Stage.
    reward: null,
    rewardName: '',
    rewardOutcome: '',
    // The payload carries totals and names nobody, so there is no face to put on this card.
    contributors: [],
  };

  const earned = count(village.earnedPoints);
  const target = count(village.pointTarget);
  if (earned !== undefined) project.value = earned;
  if (target !== undefined && target > 0) project.goal = target;

  return project;
}
